/**
 * useRandomEvent — subscribe to the currently active random sensory effect.
 *
 * Returns the most recently emitted effect, or null if none are active.
 * Components that visualize effects (Spark, StarNetwork) read this hook
 * and switch on `effect?.id`.
 *
 * Also sets up a 500ms tick so expired effects clear from the store
 * even when no taps are happening (e.g. mid-animation).
 */
import { useEffect } from 'react';
import { useRandomEventsStore, type RandomEventsStore } from './store.js';
import type { ActiveEffect } from './scheduler.js';

const selectMostRecent = (s: RandomEventsStore): ActiveEffect | null => {
  if (s.activeEffects.length === 0) return null;
  // Effects are pushed in fire order; the last one is the most recent.
  return s.activeEffects[s.activeEffects.length - 1] ?? null;
};

export function useRandomEvent(): ActiveEffect | null {
  const effect = useRandomEventsStore(selectMostRecent);

  // Tick to expire stale effects when player is idle (no taps).
  // 2026-05-26 BUG FIX: раньше `const prune = useRandomEventsStore(s => s.prune)`
  // + `[prune]` dep. Zustand не гарантирует reference stability на
  // bound methods между renders — effect мог re-init каждый рендер,
  // clearInterval+setInterval×N в секунду. Теперь читаем prune через
  // .getState() прямо в callback'е — interval ставится один раз на
  // mount, callback всегда вызывает свежий prune.
  useEffect(() => {
    const id = window.setInterval(() => {
      useRandomEventsStore.getState().prune();
    }, 500);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  return effect;
}
